import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Standardized */}
      <section className="bg-gradient-to-br from-slate-900 to-blue-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold mb-6">Privacy Policy</h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              How Enerlligence Analytics collects, processes, protects, and manages personal and business information
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 pb-6 border-b border-slate-200">
            <p className="text-slate-600 text-sm">
              Enerlligence Analytics ("Company", "We", "Us", or "Our")
            </p>
            <p className="text-slate-500 text-sm mt-2">
              Effective Date: 1 January 2026 • Last Updated: 1 January 2026
            </p>
          </div>
          <div className="prose prose-lg max-w-none text-slate-700">
            
            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">1. Purpose and Scope</h2>
            <p className="mb-4 leading-relaxed">
              This Privacy Policy outlines how Enerlligence Analytics collects, processes, stores, protects, and discloses personal and business information obtained through our websites, digital platforms, analytics services, research products, applications, and related information services (collectively, the "Services"). As a global information services provider, the Company is committed to maintaining the highest standards of data privacy, confidentiality, transparency, and responsible information governance, consistent with applicable international data protection laws and industry best practices. This Policy applies to all users, subscribers, clients, partners, and visitors interacting with our Services globally.
            </p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">2. Regulatory Compliance Framework</h2>
            <p className="mb-4 leading-relaxed">The Company processes personal data in accordance with applicable international regulations, including but not limited to:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>General Data Protection Regulation (GDPR) – European Union</li>
              <li>UK Data Protection Act</li>
              <li>Nigerian Data Protection Act (NDPA)</li>
              <li>California Consumer Privacy Act (CCPA) where applicable</li>
              <li>OECD Privacy Principles</li>
              <li>ISO 27001 Information Security standards</li>
            </ul>
            <p className="mb-4 leading-relaxed">Where local laws impose stricter requirements, such laws shall prevail.</p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">3. Information We Collect</h2>
            <p className="mb-4 leading-relaxed">We may collect and process the following categories of information:</p>
            
            <h3 className="text-lg font-semibold text-[#1F3C88] mt-6 mb-3">3.1 Personal Information</h3>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Name and professional contact details</li>
              <li>Email address and telephone number</li>
              <li>Organisation and job title</li>
              <li>Account registration information</li>
              <li>Communication preferences</li>
            </ul>

            <h3 className="text-lg font-semibold text-[#1F3C88] mt-6 mb-3">3.2 Business and Usage Information</h3>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Subscription and transaction records</li>
              <li>Platform usage analytics</li>
              <li>IP address and device identifiers</li>
              <li>Login credentials and authentication data</li>
              <li>Website interaction data</li>
            </ul>

            <h3 className="text-lg font-semibold text-[#1F3C88] mt-6 mb-3">3.3 Information Provided Voluntarily</h3>
            <p className="mb-4 leading-relaxed">Information submitted through contact forms, research subscriptions, event registrations, surveys or correspondence.</p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">4. Lawful Basis for Processing</h2>
            <p className="mb-4 leading-relaxed">Personal data is processed only where lawful grounds exist, including:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Performance of contractual obligations</li>
              <li>Legitimate business interests</li>
              <li>Legal and regulatory compliance</li>
              <li>User consent</li>
              <li>Protection of vital interests</li>
            </ul>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">5. Use of Information</h2>
            <p className="mb-4 leading-relaxed">Information collected may be used to:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Deliver research, analytics, and intelligence services</li>
              <li>Manage subscriptions and client relationships</li>
              <li>Improve platform functionality and user experience</li>
              <li>Conduct market analysis and service optimisation</li>
              <li>Communicate service updates and industry insights</li>
              <li>Meet regulatory and compliance obligations</li>
              <li>Prevent fraud, cybersecurity threats, or misuse</li>
            </ul>
            <p className="mb-4 leading-relaxed font-semibold">The Company does not sell personal data.</p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">6. Data Sharing and Disclosure</h2>
            <p className="mb-4 leading-relaxed">Information may be shared only where necessary with:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Affiliates and authorised service providers</li>
              <li>Technology and cloud infrastructure partners</li>
              <li>Professional advisers (legal, audit, compliance)</li>
              <li>Regulatory authorities where legally required</li>
            </ul>
            <p className="mb-4 leading-relaxed">All third parties are contractually required to maintain appropriate confidentiality and data protection standards.</p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">7. International Data Transfers</h2>
            <p className="mb-4 leading-relaxed">Given the global nature of our operations, data may be transferred across jurisdictions. Such transfers are conducted using appropriate safeguards including:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Standard Contractual Clauses (SCCs)</li>
              <li>Adequacy decisions</li>
              <li>Secure cloud infrastructure compliant with international standards</li>
            </ul>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">8. Data Security</h2>
            <p className="mb-4 leading-relaxed">The Company maintains robust administrative, technical, and physical safeguards designed to protect information against unauthorised access, alteration, disclosure, or destruction, including:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Encryption protocols</li>
              <li>Access control mechanisms</li>
              <li>Secure hosting environments</li>
              <li>Continuous cybersecurity monitoring</li>
              <li>Periodic security assessments</li>
            </ul>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">9. Data Retention</h2>
            <p className="mb-4 leading-relaxed">Personal data is retained only for as long as necessary to:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Fulfil contractual or service obligations</li>
              <li>Meet regulatory requirements</li>
              <li>Resolve disputes</li>
              <li>Enforce legal agreements</li>
            </ul>
            <p className="mb-4 leading-relaxed">Data is securely deleted or anonymised once retention periods expire.</p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">10. User Rights</h2>
            <p className="mb-4 leading-relaxed">Subject to applicable law, individuals may exercise the following rights:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Right of access</li>
              <li>Right to correction</li>
              <li>Right to erasure</li>
              <li>Right to restrict processing</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
              <li>Right to withdraw consent</li>
            </ul>
            <p className="mb-4 leading-relaxed">Requests may be submitted using the contact details provided below.</p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">11. Cookies and Tracking Technologies</h2>
            <p className="mb-4 leading-relaxed">
              Our digital platforms utilise cookies and similar technologies to enhance platform performance, analyse usage trends, and personalise user experience. Users may manage cookie preferences through browser settings. For more information, please see our <Link to="/cookie-policy" className="text-[#007BFF] hover:underline">Cookie Policy</Link>.
            </p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">12. Confidentiality of Industry and Client Data</h2>
            <p className="mb-4 leading-relaxed">
              As an information services organisation, the Company treats all client, research, and proprietary data with strict confidentiality and applies professional information-handling standards consistent with global advisory and research institutions.
            </p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">13. Third-Party Links</h2>
            <p className="mb-4 leading-relaxed">
              Our Services may contain links to external websites. The Company is not responsible for the privacy practices or content of third-party platforms.
            </p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">14. Policy Updates</h2>
            <p className="mb-4 leading-relaxed">
              This Privacy Policy may be updated periodically to reflect regulatory developments, technological changes, or operational requirements. Updated versions will be published on our website with the revised effective date.
            </p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">15. Contact Information</h2>
            <p className="mb-4 leading-relaxed">For privacy-related enquiries or requests:</p>
            <div className="bg-slate-50 p-6 rounded-lg border border-slate-200 mb-6">
              <p className="font-semibold text-[#1F3C88] mb-2">Data Protection Officer</p>
              <p className="text-slate-600">Enerlligence Analytics</p>
              <p className="text-slate-600">Email: <a href="mailto:privacy@enerlligence.com" className="text-[#007BFF] hover:underline">privacy@enerlligence.com</a></p>
              <p className="text-slate-600">Website: <a href="https://www.enerlligence.com" className="text-[#007BFF] hover:underline">www.enerlligence.com</a></p>
            </div>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">16. Governance Statement</h2>
            <p className="mb-4 leading-relaxed">
              The Board and Executive Management of Enerlligence Analytics maintain oversight of data governance and privacy compliance to ensure adherence to international best practices in information stewardship, ethical data usage, and stakeholder trust.
            </p>

            {/* Related Links */}
            <div className="mt-12 pt-8 border-t border-slate-200">
              <h3 className="text-lg font-semibold text-[#1F3C88] mb-4">Related Policies</h3>
              <div className="flex flex-wrap gap-4">
                <Link to="/disclaimer" className="text-[#007BFF] hover:underline">Disclaimer</Link>
                <Link to="/terms-of-service" className="text-[#007BFF] hover:underline">Terms of Service</Link>
                <Link to="/cookie-policy" className="text-[#007BFF] hover:underline">Cookie Policy</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
